<template>
  <div>
    <ehc-header
      text="Feature entitlements"
      style="max-width: 920px"
    ></ehc-header>
    <ehc-sheet class="ml-2" width="920px">
      <p class="text-caption text--secondary mb-2">
        Live Ask, Import, Branding, Custom URLs, and Pro property fields use
        this catalog. Overrides and grants apply immediately. The old column
        is the previous helper, for comparison.
      </p>
      <p class="text-caption mb-3">
        Stripe plan: <strong>{{ planKey }}</strong> · family
        <strong>{{ planFamily }}</strong>
        <span v-if="activeGrantKey">
          · elevated to <strong>{{ activeGrantKey }}</strong>
          <span v-if="grantExpiresLabel"> until {{ grantExpiresLabel }}</span>
        </span>
      </p>

      <v-alert
        v-if="activeGrantKey"
        type="info"
        dense
        text
        class="mb-4"
      >
        This organization keeps its Stripe {{ planKey }} subscription. Features
        from {{ activeGrantKey }} are unlocked
        {{ grantExpiresLabel ? "until " + grantExpiresLabel : "until you revoke the grant" }}.
        Property limit: {{ propertyLimit }}.
      </v-alert>

      <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px">
        <v-btn
          color="primary"
          small
          :disabled="saving"
          @click="openGrantDialog()"
        >
          {{ activeGrantKey ? "Change grant" : "Grant elevated features" }}
        </v-btn>
        <v-btn
          v-if="activeGrantKey"
          small
          outlined
          :disabled="saving"
          @click="$emit('revoke-grant')"
        >
          Revoke grant
        </v-btn>
      </div>

      <v-simple-table dense class="entitlements-preview-table">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Feature</th>
              <th class="text-left">Plan default</th>
              <th class="text-left">Override</th>
              <th class="text-left">Effective (new)</th>
              <th class="text-left">Effective (old)</th>
              <th class="text-left"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.featureId">
              <td>
                <div>{{ row.label }}</div>
                <div class="text-caption text--secondary">{{ row.source }}</div>
              </td>
              <td>{{ row.planDefault ? "On" : "Off" }}</td>
              <td>
                <v-btn-toggle
                  :value="row.override"
                  mandatory
                  dense
                  :disabled="saving"
                  @change="onOverrideChange(row.featureId, $event)"
                >
                  <v-btn small value="default">Default</v-btn>
                  <v-btn small value="on">On</v-btn>
                  <v-btn small value="off">Off</v-btn>
                </v-btn-toggle>
              </td>
              <td>{{ row.effectiveNew ? "On" : "Off" }}</td>
              <td>{{ row.effectiveOld ? "On" : "Off" }}</td>
              <td>
                <v-chip
                  x-small
                  :color="row.match ? 'success' : 'warning'"
                  text-color="white"
                >
                  {{ row.match ? "Match" : "Mismatch" }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </ehc-sheet>

    <v-dialog v-model="grantDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">
          Grant elevated features
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-4">
            Stripe stays on <strong>{{ planKey }}</strong>. Choose a higher
            catalog plan and how long it should last. When the grant expires,
            features return to the Stripe plan.
          </p>
          <v-select
            v-model="grantPlanKey"
            :items="grantPlanItems"
            label="Elevated plan"
            dense
            outlined
            hide-details
            class="mb-4"
          ></v-select>
          <v-select
            v-model="grantDurationDays"
            :items="durationItems"
            label="Duration"
            dense
            outlined
            hide-details
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="grantDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="saving" @click="submitGrant">
            Grant
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const {
  applyOverrideState,
  compareEntitlements,
  getPlanFamily,
  getPlanKey,
  getActiveGrantPlanKey,
  getEntitlementGrant,
  getGrantExpiresMs,
  getEffectivePropertyLimit,
  GRANT_PLAN_KEYS,
  GRANT_DURATION_OPTIONS,
  PLAN_CATALOG,
} = require("@/utils/entitlements.js");

export default {
  name: "ehc-entitlements-preview",
  props: {
    org: {
      type: Object,
      default: () => ({}),
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      grantDialog: false,
      grantPlanKey: "GuestAsk",
      grantDurationDays: 90,
    };
  },
  computed: {
    planKey() {
      return getPlanKey(this.org);
    },
    planFamily() {
      return getPlanFamily(this.org);
    },
    rows() {
      return compareEntitlements(this.org);
    },
    activeGrantKey() {
      return getActiveGrantPlanKey(this.org);
    },
    grantExpiresLabel() {
      const expiresMs = getGrantExpiresMs(getEntitlementGrant(this.org));
      if (expiresMs === null) {
        return "";
      }
      return new Date(expiresMs).toLocaleDateString();
    },
    propertyLimit() {
      return getEffectivePropertyLimit(this.org);
    },
    grantPlanItems() {
      return GRANT_PLAN_KEYS.map(function (key) {
        const plan = PLAN_CATALOG[key];
        return {
          value: key,
          text:
            (plan && plan.label) +
            " · " +
            (plan && plan.limits && plan.limits.properties) +
            " properties",
        };
      });
    },
    durationItems() {
      return GRANT_DURATION_OPTIONS.map(function (option) {
        return { value: option.days, text: option.label };
      });
    },
  },
  methods: {
    openGrantDialog(planKey) {
      this.grantPlanKey = planKey || this.activeGrantKey || "GuestAsk";
      this.grantDurationDays = 90;
      this.grantDialog = true;
    },
    submitGrant() {
      this.$emit("save-grant", {
        planKey: this.grantPlanKey,
        durationDays: this.grantDurationDays,
      });
      this.grantDialog = false;
    },
    onOverrideChange(featureId, state) {
      const current = this.rows.find((row) => row.featureId === featureId);
      if (!state || (current && current.override === state)) {
        return;
      }
      const next = applyOverrideState(
        (this.org.account && this.org.account.featureOverrides) || {},
        featureId,
        state,
      );
      this.$emit("save-overrides", next);
    },
  },
};
</script>

<style>
.entitlements-preview-table th,
.entitlements-preview-table td {
  vertical-align: middle;
}
</style>
